/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { bool, func, string, node, oneOf, oneOfType, object, element, number, shape, } from 'prop-types';
import Button from '../../atoms/button';
import setClass from '../../common/set-class';
import useOutsideClick from '../../hooks/use-outside-click';
import { ModalHeader, ModalContent, ModalFooter, Delimiter, } from './modal-parts';
const ANIMATION_OFFSET = 5;
const BACKDROP_TYPES = {
    DEFAULT: 'default',
    TRANSPARENT: 'transparent',
    NONE: 'none',
};
// If it turns out the component needs more flexibility, add children to props
// and place them inside the Modal
function Modal({ isOpen, onClose, onOpen, parts, classes, animationDuration, topOffset, portalContainer, unmountOnClose, backdropType, disableCloseOnOutsideClick, withoutCloseButton, disableScrollLock, children, }) {
    const [isHiddenCompletely, setIsHiddenCompletely] = useState(!isOpen);
    const [isOpening, setIsOpening] = useState(false);
    const modalBodyRef = useRef(null);
    const savedStyles = useRef({});
    const closeTimeoutId = useRef(null);
    const openTimeoutId = useRef(null);
    const isMounted = useRef(false);
    useEffect(() => {
        isMounted.current = true;
        return () => {
            isMounted.current = false;
        };
    }, []);
    useEffect(() => {
        const escapeHandler = (e) => {
            if (e.key === 'Escape' && isMounted.current) {
                onClose();
            }
        };
        window.addEventListener('keydown', escapeHandler);
        return () => {
            window.removeEventListener('keydown', escapeHandler);
        };
    }, [isMounted.current]);
    const { header, content, footer } = parts;
    const portalIsDisabled = portalContainer == null;
    const noBackdrop = backdropType === BACKDROP_TYPES.NONE;
    const isRenderedInBody = portalContainer?.nodeName === 'BODY';
    const propertyHolder = portalContainer || document.body;
    const clickCatcher = isRenderedInBody || portalIsDisabled ? document : portalContainer;
    const notClosed = isOpen || !isHiddenCompletely;
    useEffect(() => {
        propertyHolder.style.setProperty('--b-modal-animation-duration', `${animationDuration}ms`);
        return () => {
            propertyHolder.style.removeProperty('--b-modal-animation-duration');
        };
    }, [animationDuration, propertyHolder]);
    useEffect(() => {
        const strTopOffset = `${topOffset}`;
        const isValid = /^\d+(%|px)?$/.test(strTopOffset);
        if (isValid) {
            const value = /^\d+$/.test(strTopOffset)
                ? `${topOffset}px`
                : strTopOffset;
            propertyHolder.style.setProperty('--b-modal-top-offset', value);
        }
        return () => {
            propertyHolder.style.removeProperty('--b-modal-top-offset');
        };
    }, [topOffset, propertyHolder]);
    function resetContainerStyle(name) {
        const hasSavedValue = Object.keys(savedStyles.current).includes(name);
        if (hasSavedValue) {
            // @ts-expect-error ts-migrate(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
            propertyHolder.style.setProperty(name, savedStyles.current[name]);
            // @ts-expect-error ts-migrate(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
            delete savedStyles.current[name];
        }
    }
    function updateContainerStyle({ name, value, extraCondition = true }) {
        if (!portalIsDisabled && extraCondition && notClosed) {
            // @ts-expect-error ts-migrate(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
            savedStyles.current[name] = propertyHolder.style.getPropertyValue(name);
            propertyHolder.style.setProperty(name, value);
        }
    }
    useEffect(() => {
        updateContainerStyle({
            name: 'overflow',
            value: 'hidden',
            extraCondition: !disableScrollLock,
        });
        return () => {
            resetContainerStyle('overflow');
        };
    }, [disableScrollLock, notClosed, portalIsDisabled, portalContainer]);
    useEffect(() => {
        updateContainerStyle({ name: 'position', value: 'relative' });
        return () => {
            resetContainerStyle('position');
        };
    }, [notClosed, portalIsDisabled, portalContainer]);
    useEffect(() => {
        if (isOpen) {
            document.body.style.marginRight = '15px';
            // @ts-expect-error ts-migrate(2345) FIXME: Argument of type 'null' is not assignable to param... Remove this comment to see the full error message
            clearTimeout(closeTimeoutId.current);
            setIsOpening(true);
            setIsHiddenCompletely(false);
            // @ts-expect-error ts-migrate(2322) FIXME: Type 'number' is not assignable to type 'null'.
            openTimeoutId.current = setTimeout(() => {
                if (isMounted.current) {
                    setIsOpening(false);
                }
            }, ANIMATION_OFFSET);
            if (onOpen) {
                onOpen();
            }
        }
        else {
            // @ts-expect-error ts-migrate(2345) FIXME: Argument of type 'null' is not assignable to param... Remove this comment to see the full error message
            clearTimeout(openTimeoutId.current);
            // @ts-expect-error ts-migrate(2322) FIXME: Type 'number' is not assignable to type 'null'.
            closeTimeoutId.current = setTimeout(() => {
                if (isMounted.current) {
                    document.body.style.marginRight = '0';
                    setIsHiddenCompletely(true);
                }
            }, animationDuration + ANIMATION_OFFSET);
        }
    }, [isOpen]);
    useOutsideClick(modalBodyRef, () => {
        if (!disableCloseOnOutsideClick) {
            onClose();
        }
    }, 
    // @ts-expect-error ts-migrate(2322) FIXME: Type 'any' is not assignable to type 'never'.
    [clickCatcher, isOpen], clickCatcher);
    const trulyOpen = isOpen && !isOpening && !unmountOnClose;
    const isMounting = unmountOnClose && isOpen;
    const closedCompletely = !isOpen && !unmountOnClose && isHiddenCompletely;
    const isClosing = !isOpen && !isHiddenCompletely && unmountOnClose;
    const basicClass = {
        body: 'b-modal__body_no-backdrop',
        backdrop: `b-modal__backdrop_${backdropType}`,
    };
    function getClassList(modalPart) {
        return setClass([
            // @ts-expect-error ts-migrate(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
            basicClass[modalPart],
            closedCompletely && 'closed-completely',
            trulyOpen && 'truly-open',
            isClosing && 'with-unmount_closing',
            isMounting && 'with-unmount_opening',
            !isHiddenCompletely && 'overlaid',
            unmountOnClose && 'with-unmount_indicator',
            `b-modal__${modalPart}_in-${isRenderedInBody ? 'body' : 'custom'}`,
        ]);
    }
    const bodyClass = noBackdrop
        ? getClassList('body')
        : 'b-modal__body_with-backdrop';
    const partsBody = (React.createElement(React.Fragment, null,
        !withoutCloseButton && (React.createElement(Button, { className: "b-modal__close-button", onClick: onClose, kind: "link", icon: "xmark" })),
        header && (React.createElement(ModalHeader, { text: header.text, icon: header.icon, className: setClass([classes?.header, header.className]), children: header.children })),
        content?.children && (React.createElement(ModalContent, { className: setClass([classes?.content, content.className]) }, content.children)),
        footer?.children && (React.createElement(ModalFooter, { className: setClass([classes?.footer, footer.className]) }, footer.children))));
    const modalBody = (React.createElement("div", { className: setClass([bodyClass, classes?.root]), ref: modalBodyRef }, children ?? partsBody));
    const wrappedModal = noBackdrop ? (modalBody) : (React.createElement("div", { className: getClassList('backdrop') }, modalBody));
    const finalMolecule = portalIsDisabled
        ? wrappedModal
        : createPortal(wrappedModal, portalContainer);
    return !isOpen && unmountOnClose && isHiddenCompletely ? null : finalMolecule;
}
Modal.propTypes = {
    isOpen: bool.isRequired,
    onClose: func.isRequired,
    onOpen: func,
    parts: shape({
        header: shape({
            text: string,
            icon: string,
            className: string,
            children: node,
        }),
        content: shape({
            className: string,
            children: node,
        }),
        footer: shape({
            className: string,
            children: node,
        }),
    }),
    classes: shape({
        root: string,
        header: string,
        content: string,
        footer: string,
    }),
    animationDuration: number,
    topOffset: oneOfType([string, number]),
    portalContainer: oneOfType([node, object, element]),
    unmountOnClose: bool,
    backdropType: oneOf([...Object.values(BACKDROP_TYPES)]),
    disableCloseOnOutsideClick: bool,
    withoutCloseButton: bool,
    disableScrollLock: bool,
};
Modal.defaultProps = {
    onOpen: null,
    parts: {},
    classes: {},
    animationDuration: 300,
    topOffset: '50%',
    portalContainer: document.body,
    unmountOnClose: true,
    backdropType: BACKDROP_TYPES.DEFAULT,
    disableCloseOnOutsideClick: false,
    withoutCloseButton: false,
    disableScrollLock: false,
    children: null,
};
Modal.Header = ModalHeader;
Modal.Content = ModalContent;
Modal.Footer = ModalFooter;
Modal.Delimiter = Delimiter;
Modal.BACKDROP_TYPES = BACKDROP_TYPES;
export default Modal;
